import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledPageContainer, PageContent, SocialMediaSection } from './Pages.styles';
import Tagline from '../components/Tagline/Tagline';
import KimeraVision from '../components/KimeraVision/KimeraVision';
import { KimeraVisionRef } from '../components/KimeraVision/KimeraVision.types';
import KimeraWareSocialMedia from '../components/KimeraWareSocialMedia/KimeraWareSocialMedia';
import LearnMore from '../components/LearnMore/LearnMore';
import { SEO } from '../components/SEO/SEO';
import { StructuredData } from '../components/StructuredData/StructuredData';
import { STRUCTURED_DATA } from '../constants/seo.constants';
import tumamaVideo from '../assets/video/tumama.mp4';
import zebitasVideo from '../assets/video/zebitas.mp4';
import homeloVideo from '../assets/video/homelo.mp4';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const kimeraVisionRef = useRef<KimeraVisionRef>(null);
  const textSequenceRef = useRef<string>('');
  const konamiSequenceRef = useRef<string[]>([]);
  const textTimeoutRef = useRef<number | null>(null);
  const konamiTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const KONAMI_CODE = [
      'ArrowUp',
      'ArrowUp',
      'ArrowDown',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'ArrowLeft',
      'ArrowRight',
      'b',
      'a',
    ];

    const handleKeyPress = (event: KeyboardEvent) => {
      const key = event.key;
      const keyUpper = key.toUpperCase();

      if (textTimeoutRef.current) {
        clearTimeout(textTimeoutRef.current);
      }

      const newTextSequence = textSequenceRef.current + keyUpper;

      if ('TUMAMA'.startsWith(newTextSequence) || 'HOMERO'.startsWith(newTextSequence)) {
        textSequenceRef.current = newTextSequence;

        if (newTextSequence === 'TUMAMA') {
          if (kimeraVisionRef.current) {
            kimeraVisionRef.current.triggerEasterEgg(tumamaVideo);
          }
          textSequenceRef.current = '';
        } else if (newTextSequence === 'HOMERO') {
          if (kimeraVisionRef.current) {
            kimeraVisionRef.current.triggerEasterEgg(homeloVideo);
          }
          textSequenceRef.current = '';
        } else {
          textTimeoutRef.current = window.setTimeout(() => {
            textSequenceRef.current = '';
          }, 2000);
        }
      } else {
        textSequenceRef.current = '';
      }

      if (konamiTimeoutRef.current) {
        clearTimeout(konamiTimeoutRef.current);
      }

      const newKonamiSequence = [...konamiSequenceRef.current, key];
      const expectedSequence = KONAMI_CODE.slice(0, newKonamiSequence.length);

      let isMatching = true;
      for (let i = 0; i < newKonamiSequence.length; i++) {
        if (newKonamiSequence[i] !== expectedSequence[i]) {
          isMatching = false;
          break;
        }
      }

      if (isMatching) {
        konamiSequenceRef.current = newKonamiSequence;

        if (newKonamiSequence.length === KONAMI_CODE.length) {
          if (kimeraVisionRef.current) {
            kimeraVisionRef.current.triggerEasterEgg(zebitasVideo);
          }
          konamiSequenceRef.current = [];
        } else {
          konamiTimeoutRef.current = window.setTimeout(() => {
            konamiSequenceRef.current = [];
          }, 2000);
        }
      } else {
        konamiSequenceRef.current = [];
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (textTimeoutRef.current) {
        clearTimeout(textTimeoutRef.current);
      }
      if (konamiTimeoutRef.current) {
        clearTimeout(konamiTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledPageContainer id="home">
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        keywords={t('seo.home.keywords')}
        canonicalUrl="/"
        type="website"
      />
      <StructuredData data={STRUCTURED_DATA.ORGANIZATION} />
      <Tagline />
      <KimeraVision ref={kimeraVisionRef} />
      <PageContent>
        <LearnMore />
        <SocialMediaSection>
          <div>
            <KimeraWareSocialMedia />
          </div>
        </SocialMediaSection>
      </PageContent>
    </StyledPageContainer>
  );
};

export default HomePage;
